// useCreditBalance — Read current-period credit balance per kind för
// inloggad user (org_id = auth.uid() i dagens solo-model).

import { useQuery } from '@tanstack/react-query';
import { fromCustom } from '@/lib/supabaseTyped';
import { useAuth } from '@/contexts/AuthContext';
import type { CreditKind } from '@/lib/creditTypes';

export interface LedgerRow {
  kind: CreditKind;
  granted: number;
  consumed: number;
  reserved: number;
  refunded: number;
  balance: number;
  period_start: string;
  period_end: string;
}

async function fetchLedgers(orgId: string): Promise<LedgerRow[]> {
  const { data } = await fromCustom<LedgerRow>('credit_ledger')
    .select('kind, granted, consumed, reserved, refunded, balance, period_start, period_end')
    .eq('org_id', orgId)
    .limit(20);
  return (data ?? []) as LedgerRow[];
}

export function useCreditBalance() {
  const { user } = useAuth();
  const orgId = user?.id ?? null;

  const query = useQuery({
    queryKey: ['credit-balance', orgId],
    queryFn: () => fetchLedgers(orgId!),
    enabled: !!orgId,
    staleTime: 15_000,
  });

  const balanceByKind = (query.data ?? []).reduce<Partial<Record<CreditKind, number>>>(
    (acc, r) => { acc[r.kind] = r.balance; return acc; },
    {},
  );

  const balanceFor = (kind: CreditKind): number => balanceByKind[kind] ?? 0;

  return {
    ledgers: query.data ?? [],
    balanceByKind,
    balanceFor,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
